import { ConditionalLink } from '@/components/Common';
import FormInput from '@/components/Common/FormInput/FormInput';
import { Layout } from '@/components/Common/Layout';
import Stepper from '@/components/Common/Stepper/Stepper';
import { APP_DEEPLINK_OUTSIDE_BASE_URL, TRACKER_EVENTS } from '@/constants';
import { useAppContext } from '@/context/AppContext';
import useBreakpoints from '@/hooks/useBreakpoints';
import useMobileBackPress from '@/hooks/useMobileBackPress';
import useScrollBlur from '@/hooks/useScrollBlur';
import { IClaimCreateStepProps } from '@/pages/create';
import useClaimCreateStore, {
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import { getDimensions, track } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ArrowRight, ChevronRight } from 'ui/icons';
import { BlacklistedHospital, Button } from 'ui/mobile-ds';
import { z } from 'zod';

const Schema = ClaimCreateFormSchema.pick({
  hospitalName: true,
});
type TSchema = z.infer<typeof Schema>;

const NetworkHospitalsV1 = ({ submitFlow }: IClaimCreateStepProps) => {
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const setFormState = useClaimCreateStore((state) => state.setFormState);
  const formState = useClaimCreateStore((state) => state.formState);
  const allBenefits = useClaimCreateStore((state) => state.allBenefits);
  const [footerHeight, setFooterHeight] = useState(128);

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    // formState: { isValid },
    watch,
    setValue,
  } = useForm<TSchema>({
    resolver: zodResolver(Schema),
    defaultValues: {
      hospitalName: formState.hospitalName,
    },
  });

  const onSubmit: SubmitHandler<TSchema> = (data) => {
    setFormState({ ...data });
    submitFlow?.({
      ...formState,
      ...data,
    });
  };

  useEffect(() => {
    const footerDimensions = getDimensions('claims-footer');
    if (footerDimensions?.elem) {
      setFooterHeight(footerDimensions?.height ?? 128);
    }
  }, [formState.organisationBenefitId]);

  const [isInputFocused, setInputFocused] = useState(false);
  const { isMobile } = useBreakpoints();

  const isFooterHidden = isMobile && isInputFocused;

  const selectedBenefit = allBenefits.get(
    formState.organisationBenefitId ?? ''
  );
  const blacklistedHospitalLink =
    selectedBenefit?.plan?.blacklistedHospitals?.url;

  const trackBlacklistedHospitalClick = () => {
    track({
      event: TRACKER_EVENTS.CC_CLAIM_CREATION_BLACKLIST_VIEWED,
    });
  };

  const { isWebView } = useAppContext();

  useMobileBackPress({
    onBackPress: () => {
      if (isMobile && isInputFocused) {
        inputRef?.blur();
      } else {
        setCurrentStep(EClaimCreateFlowSteps.COLLECT_MORE_INFO);
      }
    },
    isWebView,
  });

  useScrollBlur({ inputRef });

  const handleBlackListedCTAClick = () => {
    trackBlacklistedHospitalClick();
    if (isWebView && blacklistedHospitalLink) {
      window.NativeBridge.callDeeplink(
        `${APP_DEEPLINK_OUTSIDE_BASE_URL}${blacklistedHospitalLink}`
      );
    }
  };

  return (
    <Layout.Main
      fullWidth
      headerProps={{
        title: 'Enter your network hospital',
        description:
          'Tell us where you are planning to get your treatment done',
        onBack: () => setCurrentStep(EClaimCreateFlowSteps.COLLECT_MORE_INFO),
        children: (
          <div className="flex justify-between md:hidden">
            <h4 className="text-sm text-mobile-hero-100 font-medium leading-5">
              Cashless Claim
            </h4>
            <span className="text-sm leading-5 text-mobile-text-hierarchy-2">
              Step 4/4
            </span>
          </div>
        ),
      }}
    >
      {({ getDimensions }) => {
        const onError: SubmitErrorHandler<TSchema> = () => {
          toast.error('Please fill all the mandatory fields', {
            id: 'create-claim-error',
            style: {
              marginBottom: getDimensions('claims-footer').height,
            },
          });
        };

        return (
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-1 h-full gap-14 justify-between pb-24"
          >
            <div className="flex flex-1 flex-col md:max-w-[60%]">
              <FormInput
                inputProps={{
                  ...register('hospitalName', {}),
                  ref: (ref) => {
                    setInputRef(ref);
                    register('hospitalName').ref(ref);
                  },
                  placeholder: 'Enter the name of the hospital',
                  onFocus: () => setInputFocused(true),
                  onBlur: () => setTimeout(() => setInputFocused(false), 250),
                  labelChildren: (
                    <label
                      htmlFor="dateOfAdmission"
                      className="text-sm font-medium text-mobile-text-hierarchy leading-5 after:content-['*'] after:text-primary-500"
                    >
                      Hospital name
                    </label>
                  ),
                }}
              />
            </div>
            {blacklistedHospitalLink &&
            ((isMobile && isInputFocused === false) || !isMobile) ? (
              <ConditionalLink
                external
                href={isWebView ? null : blacklistedHospitalLink}
                onClick={handleBlackListedCTAClick}
                className="bg-white border border-mobile-gray-100 transform -translate-x-1/2 md:translate-x-0 flex gap-4 px-3 py-4 md:p-8 rounded-2xl w-full md:max-w-[352px] max-w-[90%] fixed md:static items-center justify-between"
                style={{
                  bottom: footerHeight > 0 ? footerHeight + 20 : '36px',
                  left: '50%',
                }}
              >
                <div className="flex gap-4">
                  <BlacklistedHospital className="w-12 h-12 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <p className="text-gray-800 text-base md:text-xl leading-[140%]">
                      Blacklisted hospital
                    </p>
                    <p className="text-gray-500 text-sm leading-[130%]">
                      Treatment in blacklisted hospitals is not covered by your
                      insurer.
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6" />
              </ConditionalLink>
            ) : null}
            {isFooterHidden ? null : (
              <Layout.Footer isSticky containerClassName="flex justify-between">
                <Stepper
                  className="hidden md:flex flex-[0 0 min-width]"
                  activeStep={4}
                  activeSubStep={0}
                  totalSubSteps={0}
                  totalSteps={4}
                  title="Enter your network hospital"
                  subTitle="Step 3/4"
                />
                <div className="flex flex-col md:flex-row-reverse w-full mx-auto md:gap-5 gap-0">
                  <Button
                    type="submit"
                    className="min-w-[260px] md:max-w-[260px] w-full text-base"
                    onClick={(event) => {
                      const hospitalName = watch('hospitalName');
                      if (!hospitalName) {
                        event.preventDefault();
                        toast('Please enter the hospital name');
                      }
                    }}
                  >
                    Continue
                  </Button>
                  <Button
                    type="submit"
                    onClick={() => {
                      setValue('hospitalName', '');
                    }}
                    intent={'tertiary'}
                    className="min-w-fit gap-1"
                  >
                    I haven't finalised the hospital yet{' '}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </Layout.Footer>
            )}
          </form>
        );
      }}
    </Layout.Main>
  );
};

export default NetworkHospitalsV1;
